import { createEffect, createMemo, on, untrack, type Accessor } from 'solid-js';
import { useQuery, useQueryClient } from '@tanstack/solid-query';
import { asError } from '@trimble/logging';
import { useLogger } from '@trimble/solidjs-utils';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { t } from '@trimble/i18n';

import { createFxWorkspaceBreaklineStore } from './create-fx-workspace-breakline-store';
import type {
  FxBreakline,
  FxBreaklineType,
} from './fx-workspace-breakline.types';
import type { FxWorkspaceRepository } from './fx-workspace-repository';
import type { FxWorkspace, FxWorkspaceLoadStatus } from './fx-workspace.types';
import {
  FX_WORKSPACE_BREAKLINES_QUERY_KEY,
  fxWorkspaceBreaklinesQueryKey,
  toQueryLoadStatus,
  type FxWorkspaceDetailsKey,
} from './fx-workspace-query';
import { createRepositoryScopeGuard } from './fx-workspace-scope';

export interface FxWorkspaceBreaklinesApi {
  readonly loadStatus: Accessor<FxWorkspaceLoadStatus>;
  loadWorkspaceBreaklines(workspaceId: string): Promise<void>;
  reloadWorkspaceBreaklines(workspaceId: string): Promise<void>;
  listWorkspaceBreaklines(workspaceId: string): readonly FxBreakline[];
  getBreakline(
    workspaceId: string,
    type: FxBreaklineType
  ): FxBreakline | undefined;
  appendWorkspaceBreakline(workspaceId: string, breakline: FxBreakline): void;
  clearWorkspace(workspaceId: string): void;
  resetRepositoryScope(): void;
}

export function useFxWorkspaceBreaklines(options: {
  repository: Accessor<FxWorkspaceRepository>;
  fetchKey: Accessor<string | undefined>;
  workspaces: Accessor<readonly FxWorkspace[]>;
  activeWorkspaceId: Accessor<string | undefined>;
  detailsKey: Accessor<FxWorkspaceDetailsKey | undefined>;
}): FxWorkspaceBreaklinesApi {
  const logger = useLogger().getLogger('FxWorkspace');
  const queryClient = useQueryClient();
  const scope = createRepositoryScopeGuard();
  const store = createFxWorkspaceBreaklineStore({
    queryClient,
    fetchKey: options.fetchKey,
  });

  const breaklinesQuery = useQuery(() => {
    const detailsKey = options.detailsKey();
    const enabled = detailsKey !== undefined;

    return {
      enabled,
      // eslint-disable-next-line @tanstack/query/exhaustive-deps -- repository is a client instance; query identity is repositoryKey + workspaceId
      queryKey:
        detailsKey === undefined
          ? ([FX_WORKSPACE_BREAKLINES_QUERY_KEY, 'disabled'] as const)
          : fxWorkspaceBreaklinesQueryKey(
              detailsKey.key,
              detailsKey.workspaceId
            ),
      queryFn: () => {
        const workspaceId = detailsKey?.workspaceId;
        if (workspaceId === undefined) {
          return Promise.resolve([] as readonly FxBreakline[]);
        }

        return untrack(options.repository).listBreaklines(workspaceId);
      },
      retry: false,
    };
  });

  const loadStatus = createMemo(
    (): FxWorkspaceLoadStatus => toQueryLoadStatus(breaklinesQuery.status)
  );

  createEffect(
    on(
      () => ({
        workspaceId: options.detailsKey()?.workspaceId,
        data: breaklinesQuery.data,
      }),
      (snapshot) => {
        if (snapshot.workspaceId === undefined || snapshot.data === undefined) {
          return;
        }

        const workspaceId = snapshot.workspaceId;
        if (store.loadedBreaklineWorkspaceIds().has(workspaceId)) {
          return;
        }

        store.setWorkspaceBreaklineMap(workspaceId, snapshot.data);
        store.markBreaklinesLoaded(workspaceId);
      }
    )
  );

  createEffect(
    on(
      () => (breaklinesQuery.isError ? breaklinesQuery.error : undefined),
      (error, previousError) => {
        if (error === undefined || error === null || error === previousError) {
          return;
        }

        logger.error(
          'Failed to load FX workspace breaklines',
          { workspaceId: options.activeWorkspaceId() },
          asError(error)
        );
        NotificationEventBus.emit({
          type: 'error',
          message: t('mobileMapping.fxWorkspace.breaklines.loadFailed'),
        });
      }
    )
  );

  const fetchAndStoreWorkspaceBreaklines = async (
    workspaceId: string,
    loadToken: number,
    scopeSnapshot: number
  ): Promise<void> => {
    const breaklines = await options.repository().listBreaklines(workspaceId);
    if (
      !scope.isCurrent(scopeSnapshot) ||
      !store.isLatestBreaklineLoadToken(workspaceId, loadToken)
    ) {
      return;
    }

    store.setWorkspaceBreaklineMap(workspaceId, breaklines);
    store.markBreaklinesLoaded(workspaceId);
    store.cacheWorkspaceBreaklines(workspaceId, breaklines);
  };

  const loadWorkspaceBreaklines = async (
    workspaceId: string
  ): Promise<void> => {
    if (store.loadedBreaklineWorkspaceIds().has(workspaceId)) {
      return;
    }

    const workspaceExists = options
      .workspaces()
      .some((workspace) => workspace.id === workspaceId);
    if (!workspaceExists) {
      return;
    }

    const key = options.fetchKey();
    if (key === undefined) {
      return;
    }

    const loadToken = store.bumpBreaklineLoadToken(workspaceId);
    const scopeSnapshot = scope.capture();

    try {
      const breaklines = await queryClient.fetchQuery({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps -- repository is a client instance; query identity is repositoryKey + workspaceId
        queryKey: fxWorkspaceBreaklinesQueryKey(key, workspaceId),
        queryFn: () => untrack(options.repository).listBreaklines(workspaceId),
      });
      if (
        !scope.isCurrent(scopeSnapshot) ||
        !store.isLatestBreaklineLoadToken(workspaceId, loadToken)
      ) {
        return;
      }

      store.setWorkspaceBreaklineMap(workspaceId, breaklines);
      store.markBreaklinesLoaded(workspaceId);
    } catch (error: unknown) {
      if (
        !scope.isCurrent(scopeSnapshot) ||
        !store.isLatestBreaklineLoadToken(workspaceId, loadToken)
      ) {
        return;
      }

      logger.error(
        'Failed to load FX workspace breaklines',
        { workspaceId },
        asError(error)
      );
      if (options.activeWorkspaceId() !== workspaceId) {
        NotificationEventBus.emit({
          type: 'error',
          message: t('mobileMapping.fxWorkspace.breaklines.loadFailed'),
        });
      }
    }
  };

  const reloadWorkspaceBreaklines = async (
    workspaceId: string
  ): Promise<void> => {
    const loadToken = store.bumpBreaklineLoadToken(workspaceId);
    const scopeSnapshot = scope.capture();

    try {
      await fetchAndStoreWorkspaceBreaklines(
        workspaceId,
        loadToken,
        scopeSnapshot
      );
    } catch (error: unknown) {
      if (
        !scope.isCurrent(scopeSnapshot) ||
        !store.isLatestBreaklineLoadToken(workspaceId, loadToken)
      ) {
        return;
      }

      logger.error(
        'Failed to reload FX workspace breaklines',
        { workspaceId },
        asError(error)
      );
      NotificationEventBus.emit({
        type: 'error',
        message: t('mobileMapping.fxWorkspace.breaklines.loadFailed'),
      });
    }
  };

  const listWorkspaceBreaklines = (
    workspaceId: string
  ): readonly FxBreakline[] => {
    const breaklinesByType = store.workspaceBreaklines()[workspaceId];
    if (breaklinesByType === undefined) {
      return [];
    }

    return [...breaklinesByType.values()];
  };

  const getBreakline = (
    workspaceId: string,
    type: FxBreaklineType
  ): FxBreakline | undefined => {
    return store.workspaceBreaklines()[workspaceId]?.get(type);
  };

  return {
    loadStatus,
    loadWorkspaceBreaklines,
    reloadWorkspaceBreaklines,
    listWorkspaceBreaklines,
    getBreakline,
    appendWorkspaceBreakline: store.appendWorkspaceBreakline,
    clearWorkspace: store.clearWorkspace,
    resetRepositoryScope: (): void => {
      scope.invalidate();
      store.resetRepositoryScope();
    },
  };
}
